import z from "zod";

import {
  CLIENT_FLAG_REMOVAL_REASON,
  CLIENT_FLAG_REMOVAL_REASON_DISPLAY,
  ClientFlagRemovalReason,
  REMOVE_CLIENT_FLAG_OTHER_REASON_REQUIRED_MESSAGE,
  REMOVE_CLIENT_FLAG_REASON_REQUIRED_MESSAGE,
} from "../constants/client-flags";

/**
 * The Remove Flag step's form: a reason from the list, plus the clinician's own words when the
 * reason is `Other`. Both are required before a flag can come off a chart.
 */
export const ClientFlagRemovalFormSchema = z
  .object({
    reason: z.nativeEnum(CLIENT_FLAG_REMOVAL_REASON).nullable(),
    otherReason: z.string().trim(),
  })
  .superRefine(({ reason, otherReason }, context) => {
    if (!reason) {
      context.addIssue({
        path: ["reason"],
        code: z.ZodIssueCode.custom,
        message: REMOVE_CLIENT_FLAG_REASON_REQUIRED_MESSAGE,
      });
    }

    if (reason === CLIENT_FLAG_REMOVAL_REASON.Other && !otherReason) {
      context.addIssue({
        path: ["otherReason"],
        code: z.ZodIssueCode.custom,
        message: REMOVE_CLIENT_FLAG_OTHER_REASON_REQUIRED_MESSAGE,
      });
    }
  });

export type ClientFlagRemovalFormType = z.input<typeof ClientFlagRemovalFormSchema>;

export const CLIENT_FLAG_REMOVAL_INITIAL_VALUES: ClientFlagRemovalFormType = {
  reason: null,
  otherReason: "",
};

/**
 * Returns the text the flag archive's Reason column shows for a removal.
 *
 * @param reason - The removal reason (must be non-null; validation prevents null).
 * @param otherReason - The clinician's text when reason is `Other`.
 * @returns The chosen reason's label, or the trimmed text typed for `Other`. PHI when typed: it is
 *   never logged, never attached to a Sentry report, and never put in an error message.
 */
export function toClientFlagRemovalReason({
  reason,
  otherReason,
}: {
  reason: ClientFlagRemovalReason;
  otherReason: string;
}): string {
  if (reason === CLIENT_FLAG_REMOVAL_REASON.Other) return otherReason.trim();

  return CLIENT_FLAG_REMOVAL_REASON_DISPLAY[reason];
}
